// Implements: F-UIX-018, F-USG-005
/**
 * The pure arithmetic and naming of "My usage" (UIX.4). Plain data in, plain data out.
 */

import type { BackofficeScope } from "../../../types/api";
import { PLATFORM_LOCALE } from "../../../types/locale";
import type { ScopeUsageView } from "../../../types/reports";

/** What is left of one scope's month. Never negative: an overrun reads as nothing left. */
export interface Remaining {
  readonly generations: number;
  readonly budgetMicros: number;
}

/**
 * The share of the month still available in one scope.
 *
 * Runs in flight count as used: the server reserves them before they finish, so a person
 * with 9 delivered and 1 running out of 10 has nothing left to start.
 */
export function remainingOf(scope: ScopeUsageView): Remaining {
  return {
    generations: Math.max(
      0,
      scope.generations_limit - scope.generations_delivered - scope.generations_in_flight,
    ),
    budgetMicros: Math.max(0, scope.budget_limit_micros - scope.spent_micros),
  };
}

/** Who a scope row is about, for its heading. */
export type ScopeLabel =
  | { readonly kind: "personal" }
  | { readonly kind: "organization"; readonly name: string }
  /** An organization the session no longer lists (a seat lost this month): its id is not shown. */
  | { readonly kind: "formerOrganization" };

export function scopeLabelOf(
  organizationId: string | null,
  scopes: readonly BackofficeScope[],
): ScopeLabel {
  if (organizationId === null) return { kind: "personal" };
  const seat = scopes.find(
    (scope) => scope.kind === "organization" && scope.id === organizationId && scope.name !== null,
  );
  return seat?.name == null ? { kind: "formerOrganization" } : { kind: "organization", name: seat.name };
}

/** `YYYY-MM-DD`, the calendar day of a report range. */
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const DAY_TEXT = new Intl.DateTimeFormat(PLATFORM_LOCALE, {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

/**
 * `1 de octubre de 2026` for `2026-10-01`, whatever the browser's zone: the server already
 * decided the day in the zone of the month window. Anything else is returned as it came.
 */
export function calendarDayText(raw: string): string {
  const parts = DAY_RE.exec(raw);
  if (parts === null) return raw;
  const [year, month, day] = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return raw;
  return DAY_TEXT.format(date);
}
